import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, Package, Clock, ChevronRight, RefreshCw, CheckCircle, AlertTriangle } from "lucide-react";
import axios from "axios";
import { getBackendUrl } from "../utils/api";

const API = `${getBackendUrl()}/api`;

const OrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("fleety_user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    setUser(JSON.parse(storedUser));
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchOrders();
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders/${user.id}`);
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3FA9F5] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="orders-page">
      {/* Top Spacer for mobile */}
      <div className="h-6 bg-white"></div>
      
      {/* Header */}
      <header className="bg-white px-4 py-4 shadow-sm sticky top-0 z-40">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate("/home")}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100"
            data-testid="back-btn"
          >
            <ArrowLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">My Orders</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {orders.length === 0 ? (
          <div className="text-center py-12">
            <Package size={64} className="text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No orders yet</h2>
            <p className="text-gray-500 mb-6">Start ordering to see your order history</p>
            <Button
              onClick={() => navigate("/home")}
              className="bg-[#3FA9F5] hover:bg-[#2196F3] text-white"
              data-testid="browse-btn"
            >
              Browse Restaurants
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              // Determine refund status display
              const getRefundBadge = () => {
                if (order.payment_status === 'refunded' || order.refund_status === 'completed' || order.refund_status === 'success') {
                  return {
                    show: true,
                    icon: CheckCircle,
                    text: `₹${order.refund_amount || order.total} Refunded`,
                    bgColor: 'bg-green-100',
                    textColor: 'text-green-700'
                  };
                }
                if (order.refund_status === 'processing' || order.refund_status === 'pending') {
                  return {
                    show: true,
                    icon: RefreshCw,
                    text: 'Refund Processing',
                    bgColor: 'bg-yellow-100',
                    textColor: 'text-yellow-700'
                  };
                }
                if (order.refund_status === 'initiated') {
                  return {
                    show: true,
                    icon: RefreshCw,
                    text: 'Refund Initiated',
                    bgColor: 'bg-blue-100',
                    textColor: 'text-blue-700'
                  };
                }
                if (order.refund_status === 'failed') {
                  return {
                    show: true,
                    icon: AlertTriangle,
                    text: 'Refund Failed',
                    bgColor: 'bg-red-100',
                    textColor: 'text-red-700'
                  };
                }
                return { show: false };
              };
              
              const refundBadge = getRefundBadge();
              const RefundIcon = refundBadge.icon;
              
              return (
              <div 
                key={order.id} 
                className="bg-white rounded-xl shadow-sm overflow-hidden"
                data-testid={`order-${order.id}`}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-xs text-[#3FA9F5] font-medium mb-1">Order #{order.order_number || "N/A"}</p>
                      <h3 className="font-semibold text-gray-900">{order.restaurant_name}</h3>
                      <p className="text-sm text-gray-500">
                        {order.items?.length} items • ₹{order.total}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        order.status === 'placed' 
                          ? 'bg-green-100 text-green-700'
                          : order.status === 'delivered'
                          ? 'bg-blue-100 text-blue-700'
                          : order.status === 'cancelled'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </span>
                      {/* Refund Status Badge */}
                      {refundBadge.show && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${refundBadge.bgColor} ${refundBadge.textColor}`}>
                          <RefundIcon size={12} className={order.refund_status === 'processing' || order.refund_status === 'pending' ? 'animate-spin' : ''} />
                          {refundBadge.text}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                    <Clock size={14} />
                    <span>{formatDate(order.created_at)}</span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {order.items?.slice(0, 3).map((item, index) => (
                      <span key={index} className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {item.name} × {item.quantity}
                      </span>
                    ))}
                    {order.items?.length > 3 && (
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                        +{order.items.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="border-t border-gray-100 px-4 py-3 flex items-center justify-between bg-gray-50">
                  <span className="text-sm text-gray-500">Order ID: #{order.order_number || "N/A"}</span>
                  <button 
                    className="text-[#3FA9F5] text-sm font-medium flex items-center gap-1"
                    onClick={() => navigate(`/order-success/${order.id}`)}
                  >
                    View Details
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
